import { useEffect, useRef, useCallback } from 'react';

type MusicIntensity = 'calm' | 'waiting' | 'medium' | 'intense' | 'critical' | 'victory' | 'defeat';

interface OscillatorNode extends AudioScheduledSourceNode {
  frequency: AudioParam;
  detune: AudioParam;
  type: OscillatorType;
}

export const useBackgroundMusic = () => {
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorsRef = useRef<OscillatorNode[]>([]);
  const gainNodesRef = useRef<GainNode[]>([]);
  const masterGainRef = useRef<GainNode | null>(null);
  const filterRef = useRef<BiquadFilterNode | null>(null);
  const isPlayingRef = useRef(false);
  const currentIntensityRef = useRef<MusicIntensity>('calm');

  const intensityParams = {
    calm: { baseFreq: 55, tempo: 60, layers: 1, filterFreq: 300 },
    waiting: { baseFreq: 82, tempo: 80, layers: 2, filterFreq: 500 },
    medium: { baseFreq: 110, tempo: 100, layers: 3, filterFreq: 800 },
    intense: { baseFreq: 147, tempo: 130, layers: 4, filterFreq: 1200 },
    critical: { baseFreq: 196, tempo: 160, layers: 4, filterFreq: 2000 },
    victory: { baseFreq: 220, tempo: 140, layers: 4, filterFreq: 3000 },
    defeat: { baseFreq: 65, tempo: 70, layers: 2, filterFreq: 200 },
  };

  const initAudioContext = useCallback(() => {
    if (audioContextRef.current) return;

    try {
      const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
      audioContextRef.current = new AudioContext();

      // Create master gain node
      masterGainRef.current = audioContextRef.current.createGain();
      masterGainRef.current.gain.value = 0.15; // Low volume for background music
      masterGainRef.current.connect(audioContextRef.current.destination);

      // Create filter
      filterRef.current = audioContextRef.current.createBiquadFilter();
      filterRef.current.type = 'lowpass';
      filterRef.current.frequency.value = 500;
      filterRef.current.connect(masterGainRef.current);

      // Create oscillators and gain nodes for layers
      for (let i = 0; i < 4; i++) {
        const oscillator = audioContextRef.current.createOscillator() as OscillatorNode;
        const gainNode = audioContextRef.current.createGain();
        
        gainNode.gain.value = 0;
        oscillator.connect(gainNode);
        gainNode.connect(filterRef.current);
        
        oscillatorsRef.current.push(oscillator);
        gainNodesRef.current.push(gainNode);
      }

      // Set oscillator types for different layers
      oscillatorsRef.current[0].type = 'sine'; // Base drone
      oscillatorsRef.current[1].type = 'square'; // Rhythm
      oscillatorsRef.current[2].type = 'triangle'; // Melody
      oscillatorsRef.current[3].type = 'sawtooth'; // Harmonics
    } catch (error) {
      console.warn('Web Audio API not supported:', error);
    }
  }, []);

  const start = useCallback(() => {
    if (isPlayingRef.current || !audioContextRef.current) return;

    try {
      // Start all oscillators
      oscillatorsRef.current.forEach((osc, i) => {
        try {
          osc.start();
        } catch (e) {
          // Oscillator already started
        }
      });

      isPlayingRef.current = true;
      setIntensity('calm');
    } catch (error) {
      console.warn('Could not start music:', error);
    }
  }, []);

  const stop = useCallback(() => {
    if (!isPlayingRef.current || !audioContextRef.current) return;

    const fadeTime = 1.0;
    const now = audioContextRef.current.currentTime;

    gainNodesRef.current.forEach(gain => {
      gain.gain.exponentialRampToValueAtTime(0.001, now + fadeTime);
    });

    setTimeout(() => {
      isPlayingRef.current = false;
    }, fadeTime * 1000);
  }, []);

  const setIntensity = useCallback((intensity: MusicIntensity) => {
    if (!audioContextRef.current || !isPlayingRef.current) return;

    currentIntensityRef.current = intensity;
    const params = intensityParams[intensity];
    const now = audioContextRef.current.currentTime;
    const transitionTime = 2.0;

    // Update filter
    if (filterRef.current) {
      filterRef.current.frequency.exponentialRampToValueAtTime(
        params.filterFreq,
        now + transitionTime
      );
    }

    // Update oscillator frequencies and gains
    oscillatorsRef.current.forEach((osc, i) => {
      const multiplier = i === 0 ? 1 : Math.pow(1.5, i); // Harmonic series
      const targetFreq = params.baseFreq * multiplier;
      
      osc.frequency.exponentialRampToValueAtTime(
        targetFreq,
        now + transitionTime
      );

      // Set gain based on layer activity
      const targetGain = i < params.layers ? 0.15 / params.layers : 0.001;
      gainNodesRef.current[i].gain.exponentialRampToValueAtTime(
        targetGain,
        now + transitionTime
      );
    });
  }, []);

  const setVolume = useCallback((volume: number) => {
    if (!audioContextRef.current || !masterGainRef.current) return;

    const clampedVolume = Math.max(0, Math.min(1, volume));
    const now = audioContextRef.current.currentTime;
    masterGainRef.current.gain.exponentialRampToValueAtTime(
      clampedVolume * 0.15 + 0.001,
      now + 0.5
    );
  }, []);

  // Initialize on mount
  useEffect(() => {
    initAudioContext();

    // Resume audio context on user interaction (required for autoplay policy)
    const handleInteraction = () => {
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume();
      }
    };

    document.addEventListener('click', handleInteraction, { once: true });
    document.addEventListener('keydown', handleInteraction, { once: true });

    return () => {
      if (isPlayingRef.current) {
        stop();
      }
    };
  }, [initAudioContext, stop]);

  return {
    start,
    stop,
    setIntensity,
    setVolume,
    isPlaying: isPlayingRef.current,
  };
};
